// EducateurDashboard.jsx — Interface Éducateur : mes groupes + faire l'appel.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Users, MapPin, Clock, CalendarDays, ArrowLeft, Loader2, CheckCircle2, Save, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { RoleShell } from './RoleShell';

const STATUTS = [
  { key: 'present', label: 'Présent', color: '#10B981' },
  { key: 'absent', label: 'Absent', color: '#EF4444' },
  { key: 'excuse', label: 'Excusé', color: '#F59E0B' },
];

const JOURS_FR = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
function nextSeanceLabel(jour) {
  const idx = JOURS_FR.indexOf((jour || '').trim().toLowerCase());
  if (idx < 0) return '';
  const target = (idx + 1) % 7; // JS getDay: 0=dimanche
  const today = new Date();
  const d = new Date(today);
  d.setDate(today.getDate() + ((target - today.getDay() + 7) % 7));
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

export default function EducateurDashboard() {
  const { user } = useAuth();
  const accent = user?.couleur_principale || '#10B981';
  const [groupes, setGroupes] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/educateur/groupes').then(({ data }) => setGroupes(data)).catch(() => setGroupes([]));
  }, []);

  if (selected) {
    return <GroupeAppel groupeId={selected} accent={accent} onBack={() => setSelected(null)} />;
  }

  return (
    <RoleShell roleLabel="Éducateur" title="Mes groupes" subtitle="Consultez vos effectifs et faites l'appel de vos séances." testid="educateur-dashboard">
      {groupes === null ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[0, 1].map((i) => <div key={i} className="h-40 rounded-2xl bg-white border border-[#F1F5F9] animate-pulse" />)}
        </div>
      ) : groupes.length === 0 ? (
        <EmptyState icon={<Users className="w-8 h-8" />} title="Aucun groupe" text="Aucun groupe ne vous est encore attribué." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="educateur-groupes-list">
          {groupes.map((g) => (
            <button
              key={g.id}
              onClick={() => setSelected(g.id)}
              className="text-left bg-white border-2 border-[#F1F5F9] rounded-2xl p-5 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
              data-testid={`groupe-card-${g.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-black text-xl text-[#0F172A]">{g.nom}</h3>
                {g.categorie && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white" style={{ background: accent }}>{g.categorie}</span>}
              </div>
              <div className="space-y-1.5 text-sm text-[#475569]">
                <div className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: accent }} /> {g.nb_joueurs} joueur·s</div>
                {(g.jour || g.heure) && <div className="flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: accent }} /> {g.jour} {g.heure}</div>}
                {g.lieu && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: accent }} /> {g.lieu}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-[#F1F5F9] text-xs font-bold flex items-center gap-1.5" style={{ color: accent }}>
                <CalendarDays className="w-3.5 h-3.5" /> {g.dernier_appel ? `Dernier appel : ${g.dernier_appel}` : 'Aucun appel enregistré'}
              </div>
            </button>
          ))}
        </div>
      )}
    </RoleShell>
  );
}

function GroupeAppel({ groupeId, accent, onBack }) {
  const [g, setG] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [presence, setPresence] = useState({});
  const [saving, setSaving] = useState(false);
  const [convoking, setConvoking] = useState(false);

  const convoquer = async () => {
    setConvoking(true);
    try {
      const { data } = await api.post(`/educateur/groupe/${groupeId}/convoquer`);
      const total = data.sent + data.mocked;
      toast.success(total === 0
        ? 'Aucun parent à convoquer (rattachez des parents aux joueurs).'
        : (data.mocked && !data.sent ? `Convocation simulée (email non configuré) — ${total} parent·s` : `Convocation envoyée à ${total} parent·s`));
    } catch { toast.error('Convocation impossible'); }
    finally { setConvoking(false); }
  };

  useEffect(() => {
    api.get(`/educateur/groupe/${groupeId}`).then(({ data }) => {
      setG(data);
      const init = {};
      (data.joueurs || []).forEach((j) => { init[j.id] = 'present'; });
      setPresence(init);
    }).catch(() => toast.error('Impossible de charger le groupe'));
  }, [groupeId]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/educateur/groupe/${groupeId}/appel`, { date, presence });
      toast.success('Appel enregistré ✓');
      const { data } = await api.get(`/educateur/groupe/${groupeId}`);
      setG(data);
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const counts = Object.values(presence).reduce((a, s) => { a[s] = (a[s] || 0) + 1; return a; }, {});

  return (
    <RoleShell roleLabel="Éducateur" title={g ? g.nom : 'Chargement…'} subtitle="Faire l'appel de la séance" testid="educateur-appel">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#475569] hover:text-[#0F172A] mb-5" data-testid="appel-back-btn">
        <ArrowLeft className="w-4 h-4" /> Retour aux groupes
      </button>

      {g && (
        <div className="mb-5">
          <button onClick={convoquer} disabled={convoking} className="inline-flex items-center gap-2 text-sm font-bold text-white rounded-xl px-4 py-2.5 disabled:opacity-60" style={{ background: accent }} data-testid="convoquer-btn">
            {convoking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Convoquer les parents{nextSeanceLabel(g.jour) ? ` — ${nextSeanceLabel(g.jour)}` : ''}
          </button>
        </div>
      )}

      {!g ? (
        <div className="h-64 rounded-2xl bg-white border border-[#F1F5F9] animate-pulse" />
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Appel */}
          <div className="lg:col-span-2 bg-white border-2 border-[#F1F5F9] rounded-2xl p-5">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <label className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
                <CalendarDays className="w-4 h-4" style={{ color: accent }} /> Date de la séance
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="ml-2 border-2 border-[#F1F5F9] rounded-lg px-3 py-1.5 text-sm font-semibold" data-testid="appel-date" />
              </label>
              <div className="text-xs text-[#475569]">
                <span className="font-bold text-[#10B981]">{counts.present || 0} présents</span> · {counts.absent || 0} absents · {counts.excuse || 0} excusés
              </div>
            </div>

            <div className="divide-y divide-[#F1F5F9]" data-testid="appel-roster">
              {g.joueurs.map((j) => (
                <div key={j.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="font-semibold text-[#0F172A]">{j.prenom} {j.nom}</div>
                  <div className="flex gap-1.5">
                    {STATUTS.map((s) => {
                      const active = presence[j.id] === s.key;
                      return (
                        <button
                          key={s.key}
                          onClick={() => setPresence((p) => ({ ...p, [j.id]: s.key }))}
                          className="text-xs font-bold px-3 py-1.5 rounded-lg border-2 transition-all"
                          style={active ? { background: s.color, borderColor: s.color, color: '#fff' } : { borderColor: '#F1F5F9', color: '#94A3B8' }}
                          data-testid={`presence-${j.id}-${s.key}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={submit}
              disabled={saving}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 text-white font-bold rounded-xl py-3 disabled:opacity-60"
              style={{ background: accent }}
              data-testid="appel-submit"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Enregistrer l&apos;appel
            </button>
          </div>

          {/* Historique */}
          <div className="bg-white border-2 border-[#F1F5F9] rounded-2xl p-5">
            <h3 className="font-display font-black text-[#0F172A] mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" style={{ color: accent }} /> Séances récentes</h3>
            {(!g.appels || g.appels.length === 0) ? (
              <p className="text-sm text-[#94A3B8]">Aucune séance enregistrée.</p>
            ) : (
              <ul className="space-y-2" data-testid="appel-history">
                {g.appels.map((a) => {
                  const vals = Object.values(a.presence || {});
                  const p = vals.filter((v) => v === 'present').length;
                  return (
                    <li key={a.id} className="flex items-center justify-between text-sm border border-[#F1F5F9] rounded-lg px-3 py-2">
                      <span className="font-semibold text-[#0F172A]">{a.date}</span>
                      <span className="text-[#475569]">{p}/{vals.length} présents</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </RoleShell>
  );
}

function EmptyState({ icon, title, text }) {
  return (
    <div className="bg-white border-2 border-dashed border-[#E2E8F0] rounded-2xl p-10 text-center">
      <div className="w-14 h-14 rounded-2xl bg-[#F1F5F9] grid place-items-center mx-auto mb-3 text-[#94A3B8]">{icon}</div>
      <h3 className="font-display font-black text-[#0F172A]">{title}</h3>
      <p className="text-sm text-[#475569] mt-1">{text}</p>
    </div>
  );
}
